// next/dynamic compat shim — React.lazy + Suspense. Honors the { loading }
// fallback; ignores { ssr } (no SSR in the probe).
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';

type Loader<P> = () => Promise<{ default: ComponentType<P> } | ComponentType<P>>;
type DynamicOptions = {
  ssr?: boolean;
  loading?: (props: { error?: Error | null; isLoading?: boolean; pastDelay?: boolean }) => ReactNode;
};

export default function dynamic<P extends object>(loader: Loader<P>, options: DynamicOptions = {}) {
  const Lazy = lazy(async () => {
    const mod = await loader();
    return 'default' in (mod as { default?: unknown }) && (mod as { default?: unknown }).default
      ? (mod as { default: ComponentType<P> })
      : { default: mod as ComponentType<P> };
  });
  const Fallback = options.loading ? () => <>{options.loading!({ isLoading: true })}</> : () => null;
  return function DynamicComponent(props: P) {
    return (
      <Suspense fallback={<Fallback />}>
        <Lazy {...(props as P)} />
      </Suspense>
    );
  };
}
